import { supabase } from '../client/browser'
import { auditService } from './audit'
import { v4 as uuidv4 } from 'uuid'
import type { SupabaseClient } from '@supabase/supabase-js'

import { ProfileData, ProfileSection } from '../types/profile'
import { PublicTherapistProfile, AREA_OF_FOCUS_CATEGORIES, MODALITY_DESCRIPTIONS, TIMEZONE_DISPLAY_NAMES } from '../types/public-therapist'

/**
 * Profile service for all Pro table operations
 */

export const profileService = {
  /**
   * Get all therapist profiles (for admin use)
   */
  async getAll(): Promise<{ id: string; authId: string; firstName: string | null; lastName: string | null; email: string | null; cohort: string | null; isTherapist: boolean | null; isSupervisor: boolean | null; employmentStatus: string | null }[]> {
    try {
      const { data, error } = await supabase
        .from('pros')
        .select('id, authId, firstName, lastName, email, cohort, isTherapist, isSupervisor, employmentStatus')
        .eq('isActive', true)
        .order('firstName', { ascending: true })
        .order('lastName', { ascending: true })

      if (error) {
        console.error('Error fetching all profiles:', error)
        return []
      }

      return data || []
    } catch (err) {
      console.error('Error in getAll:', err)
      return []
    }
  },

  /**
   * Get therapists supervised by a specific supervisor
   */
  async getSupervised(supervisorId: string): Promise<{ id: string; authId: string; firstName: string | null; lastName: string | null; email: string | null; cohort: string | null; isTherapist: boolean | null; isSupervisor: boolean | null; employmentStatus: string | null }[]> {
    try {
      const { data, error } = await supabase
        .from('pros')
        .select('id, authId, firstName, lastName, email, cohort, isTherapist, isSupervisor, employmentStatus')
        .eq('supervisorId', supervisorId)
        .eq('isActive', true)
        .order('firstName', { ascending: true })
        .order('lastName', { ascending: true })

      if (error) {
        console.error('Error fetching supervised therapists:', error)
        return []
      }

      return data || []
    } catch (err) {
      console.error('Error in getSupervised:', err)
      return []
    }
  },

  /**
   * Get all therapists (isTherapist = true)
   */
  async getAllTherapists(): Promise<{ id: string; authId: string; firstName: string | null; lastName: string | null; email: string | null; cohort: string | null; isTherapist: boolean | null; isSupervisor: boolean | null; employmentStatus: string | null }[]> {
    try {
      const { data, error } = await supabase
        .from('pros')
        .select('id, authId, firstName, lastName, email, cohort, isTherapist, isSupervisor, employmentStatus')
        .eq('isTherapist', true)
        .eq('isActive', true)
        .order('firstName', { ascending: true })
        .order('lastName', { ascending: true })

      if (error) {
        console.error('Error fetching all therapists:', error)
        return []
      }

      return data || []
    } catch (err) {
      console.error('Error in getAllTherapists:', err)
      return []
    }
  },

  /**
   * Get all residents (employmentStatus = RESIDENT)
   */
  async getResidents(): Promise<{ id: string; authId: string; firstName: string | null; lastName: string | null; email: string | null; cohort: string | null; isTherapist: boolean | null; isSupervisor: boolean | null; employmentStatus: string | null }[]> {
    try {
      const { data, error } = await supabase
        .from('pros')
        .select('id, authId, firstName, lastName, email, cohort, isTherapist, isSupervisor, employmentStatus')
        .eq('employmentStatus', 'RESIDENT')
        .eq('isActive', true)
        .order('firstName', { ascending: true })
        .order('lastName', { ascending: true })

      if (error) {
        console.error('Error fetching residents:', error)
        return []
      }

      return data || []
    } catch (err) {
      console.error('Error in getResidents:', err)
      return []
    }
  },

  /**
   * Get minimal profile data (id, firstName, timezone, isApprovedForCouples, googleMeetLink, isAcceptingIndividual, isAcceptingCouples)
   */
  async getMinimalByAuthId(authId: string): Promise<{ id: string; firstName: string | null; lastName: string | null; timezone: string | null; isApprovedForCouples: boolean | null; googleMeetLink: string | null; isAcceptingIndividual: boolean | null; isAcceptingCouples: boolean | null; email: string | null; isAdmin: boolean | null; isTherapist: boolean | null; isSupervisor: boolean | null; isSeniorSupervisor: boolean | null; isBetweenSession: boolean | null; employmentStatus: string | null; payRate: number | null; crpoRegistrationDate: string | null } | null> {
    try {
      const { data, error } = await supabase
        .from('pros')
        .select('id, firstName, lastName, timezone, isApprovedForCouples, googleMeetLink, isAcceptingIndividual, isAcceptingCouples, email, isAdmin, isTherapist, isSupervisor, isSeniorSupervisor, isBetweenSession, employmentStatus, payRate, crpoRegistrationDate')
        .eq('authId', authId)
        .maybeSingle()

      if (error) {
        console.error('Error fetching minimal profile:', error)
        return null
      }

      return data
    } catch (err) {
      console.error('Error in getMinimalByAuthId:', err)
      return null
    }
  },

  /**
   * Get a profile by auth ID
   */
  async getByAuthId(authId: string, client?: SupabaseClient): Promise<ProfileData | null> {
    const db = client || supabase
    try {
      const { data, error } = await db
        .from('pros')
        .select('*')
        .eq('authId', authId)
        .maybeSingle()

      if (error) {
        console.error('Error fetching profile:', error)
        return null
      }

      console.log('[getByAuthId] Raw profile data from DB:', {
        authId,
        profileImage: data?.profileImage,
        hasData: !!data
      })

      if (data) {
        const transformed = transformToProfileData(data)
        
        // Check if we had to clean the schedule data
        if (data.schedule && JSON.stringify(data.schedule) !== JSON.stringify(transformed.schedule)) {
          console.log('[getByAuthId] Auto-fixing corrupted schedule data for user:', authId)
          
          // Auto-fix the corrupted data in the database (non-blocking)
          ;(async () => {
            try {
              const { error: updateError } = await db
                .from('pros')
                .update({ schedule: transformed.schedule })
                .eq('authId', authId)
              
              if (updateError) {
                console.error('[getByAuthId] Failed to auto-fix schedule:', updateError)
              } else {
                console.log('[getByAuthId] Successfully auto-fixed schedule data')
              }
            } catch (err) {
              console.error('[getByAuthId] Error during auto-fix:', err)
            }
          })()
        }
        
        return transformed
      }
      
      return null
    } catch (err) {
      console.error('Error in getByAuthId:', err)
      return null
    }
  },

  /**
   * Create a new profile
   */
  async create(authId: string, email: string, data: Partial<ProfileData>, client?: SupabaseClient) {
    const db = client || supabase
    const { data: newProfile, error } = await db
      .from('pros')
      .insert({
        id: uuidv4(),
        authId,
        email,
        firstName: data.firstName || '',
        ...mapProfileDataToDb(data)
      })
      .select()
      .single()

    if (error) throw error
    return transformToProfileData(newProfile)
  },

  /**
   * Update an existing profile
   */
  async update(authId: string, data: Partial<ProfileData>, client?: SupabaseClient) {
    const db = client || supabase
    console.log('[profileService.update] Updating with data:', data)

    // Fetch current profile data for audit logging
    let currentProfile = null;
    try {
      const { data: current } = await db
        .from('pros')
        .select('*')
        .eq('authId', authId)
        .single()
      currentProfile = current
    } catch (err) {
      console.warn('[profileService.update] Could not fetch current profile for audit:', err)
    }

    const mappedData = mapProfileDataToDb(data)
    console.log('[profileService.update] Mapped data for DB:', mappedData)

    // Special logging for profileImage
    if (data.profileImage || mappedData.profileImage) {
      console.log('[profileService.update] Profile image update:', {
        inputProfileImage: data.profileImage,
        mappedProfileImage: mappedData.profileImage
      })
    }

    const { data: updated, error } = await db
      .from('pros')
      .update(mappedData)
      .eq('authId', authId)
      .select()
      .single()

    if (error) throw error

    console.log('[profileService.update] Updated profile data:', {
      profileImage: updated.profileImage
    })

    // Log profile update for HIPAA compliance with before/after values (non-blocking)
    if (authId && updated && currentProfile) {
      try {
        // Build changes object showing old vs new values
        const changes: Record<string, { old: any, new: any }> = {}
        for (const key of Object.keys(mappedData)) {
          if (currentProfile[key] !== updated[key]) {
            changes[key] = {
              old: currentProfile[key],
              new: updated[key]
            }
          }
        }

        auditService.logProAction(
          updated.id,
          'profile.update',
          'profile',
          updated.id,
          {
            updatedFields: Object.keys(data),
            changes: changes,
            timestamp: new Date().toISOString()
          },
          db
        ).catch(err => {
          console.error('[profileService] Failed to log profile update:', err);
        });
      } catch (err) {
        console.error('[profileService] Failed to log profile update:', err);
      }
    }

    return transformToProfileData(updated)
  },

  /**
   * Update or create a profile (upsert)
   */
  async upsert(authId: string, email: string, data: Partial<ProfileData>, client?: SupabaseClient) {
    const existing = await this.getByAuthId(authId, client)

    if (existing) {
      return this.update(authId, data, client)
    } else {
      return this.create(authId, email, data, client)
    }
  },

  /**
   * Update a specific section of the profile
   */
  async updateSection(authId: string, email: string, section: ProfileSection, data: any, client?: SupabaseClient) {
    const sectionData = mapSectionToProfileData(section, data)
    return this.upsert(authId, email, sectionData, client)
  },

  /**
   * Delete a profile (soft delete)
   */
  async delete(authId: string) {
    const { error } = await supabase
      .from('pros')
      .update({ deletedAt: new Date().toISOString() })
      .eq('authId', authId)

    if (error) throw error
  },

  /**
   * Get all active supervisors
   */
  async getSupervisors() {
    try {
      const { data, error } = await supabase
        .from('pros')
        .select('id, firstName, lastName')
        .eq('isSupervisor', true)
        .eq('isActive', true)
        .is('deletedAt', null)
        .order('firstName', { ascending: true })

      if (error) {
        console.error('Error fetching supervisors:', error)
        return []
      }

      return data || []
    } catch (err) {
      console.error('Error in getSupervisors:', err)
      return []
    }
  },

  /**
   * Upload profile image to Supabase Storage
   */
  async uploadProfileImage(userId: string, file: File, client?: SupabaseClient): Promise<string | null> {
    const db = client || supabase
    try {
      // Create a simple filename with just the extension
      const fileExt = file.name.split('.').pop()
      // Add timestamp to filename to avoid duplicate issues
      const timestamp = Date.now()
      const fileName = `headshot-${timestamp}.${fileExt}`
      const filePath = `profile-images/${userId}/${fileName}`

      // Old headshot files are intentionally NOT deleted. The profile-photo-change
      // review email (see profileService.update below) links the previous URL so
      // reviewers can compare before/after — deleting immediately would leave the
      // "before" link dead by the time they open the email. Storage cost is
      // negligible; run a periodic cleanup job if this grows.

      console.log('[uploadProfileImage] Uploading file:', {
        userId,
        filePath,
        bucket: 'public-assets',
        fullPath: `public-assets/${filePath}`
      })

      // Upload to therapist's folder in storage (folder is created automatically if it doesn't exist)
      const { data, error } = await db.storage
        .from('public-assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        })

      if (error) {
        console.error('Error uploading image:', {
          error: error.message,
          filePath,
          fileSize: file.size,
          fileType: file.type
        })
        
        // If it's a duplicate file error, try to delete and re-upload
        if (error.message && error.message.includes('duplicate')) {
          console.log('[uploadProfileImage] Attempting to delete existing file and retry...')

          // Try to delete the existing file first
          const { error: deleteError } = await db.storage
            .from('public-assets')
            .remove([filePath])

          if (deleteError) {
            console.error('Error deleting existing file:', deleteError)
          }

          // Retry upload
          const { data: retryData, error: retryError } = await db.storage
            .from('public-assets')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: false // Use false this time since we deleted
            })
          
          if (retryError) {
            console.error('Error on retry upload:', retryError)
            throw new Error(`Failed to upload image: ${retryError.message}`)
          }
          
          console.log('[uploadProfileImage] Retry upload successful:', retryData)
        } else {
          throw new Error(`Failed to upload image: ${error.message}`)
        }
      }

      console.log('[uploadProfileImage] Upload successful:', data)

      // Get public URL
      const { data: { publicUrl } } = db.storage
        .from('public-assets')
        .getPublicUrl(filePath)

      console.log('[uploadProfileImage] Public URL:', publicUrl)

      // Add cache-busting parameter to force refresh
      const urlWithCacheBuster = `${publicUrl}?t=${Date.now()}`
      console.log('[uploadProfileImage] URL with cache buster:', urlWithCacheBuster)

      // Log profile image upload for HIPAA compliance (non-blocking)
      try {
        // userId here is authId — look up pros.id for audit FK
        const { data: proRecord } = await db
          .from('pros')
          .select('id')
          .eq('authId', userId)
          .maybeSingle()
        const proId = proRecord?.id
        if (proId) {
          auditService.logProAction(
            proId,
            'document.upload',
            'document',
            proId,
            {
              documentType: 'profileImage',
              filePath: filePath,
              fileName: fileName,
              fileSize: file.size,
              fileType: file.type,
              timestamp: new Date().toISOString()
            },
            db
          ).catch(err => {
            console.error('[uploadProfileImage] Failed to log upload:', err);
          });
        }
      } catch (err) {
        console.error('[uploadProfileImage] Failed to log upload:', err);
      }

      return urlWithCacheBuster
    } catch (err) {
      console.error('Error in uploadProfileImage:', err)
      return null
    }
  },

  /**
   * Upload a document for a therapist
   */
  async uploadDocument(proId: string, file: File, documentType: 'backgroundCheck' | 'crpoRegistration' | 'liabilityInsurance', client?: SupabaseClient): Promise<string | null> {
    const db = client || supabase
    try {
      // No need to check auth - Supabase will handle it when we try to upload
      // Get file extension and add timestamp to filename
      const fileExt = file.name.split('.').pop()
      const timestamp = Date.now()
      const fileName = `${documentType}-${timestamp}.${fileExt}`
      const filePath = `${proId}/${fileName}`

      // Also try to clean up old document files of the same type
      try {
        const { data: existingFiles } = await db.storage
          .from('therapist-documents')
          .list(proId, {
            search: documentType
          })

        if (existingFiles && existingFiles.length > 0) {
          console.log('[uploadDocument] Found existing document files:', existingFiles.map(f => f.name))
          // Delete old document files of the same type
          const filesToDelete = existingFiles
            .filter(f => f.name.startsWith(`${documentType}-`) || f.name.startsWith(`${documentType}.`))
            .map(f => `${proId}/${f.name}`)

          if (filesToDelete.length > 0) {
            const { error: deleteError } = await db.storage
              .from('therapist-documents')
              .remove(filesToDelete)
            
            if (deleteError) {
              console.warn('[uploadDocument] Error deleting old files:', deleteError)
            } else {
              console.log('[uploadDocument] Deleted old document files:', filesToDelete)
            }
          }
        }
      } catch (listError) {
        console.warn('[uploadDocument] Error listing existing files:', listError)
      }
      
      console.log('[uploadDocument] Uploading document:', { 
        proId, 
        documentType, 
        fileName,
        filePath,
        fileSize: file.size,
        fileType: file.type
      })

      // Upload the new file with unique filename
      const { error } = await db.storage
        .from('therapist-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false  // No need for upsert with unique filenames
        })

      if (error) {
        console.error('[uploadDocument] Upload error:', {
          error: error.message,
          filePath,
          fileSize: file.size,
          fileType: file.type
        })
        throw new Error(`Failed to upload document: ${error.message}`)
      }
      
      console.log('[uploadDocument] Upload successful:', { filePath })

      // Log document upload for HIPAA compliance (non-blocking)
      try {
        auditService.logProAction(
          proId,
          'document.upload',
          'document',
          proId,
          {
            documentType: documentType,
            filePath: filePath,
            fileName: fileName,
            fileSize: file.size,
            fileType: file.type,
            timestamp: new Date().toISOString()
          },
          db
        ).catch(err => {
          console.error('[uploadDocument] Failed to log upload:', err);
        });
      } catch (err) {
        console.error('[uploadDocument] Failed to log upload:', err);
      }

      // Return just the file path
      return filePath
    } catch (err: any) {
      console.error('[uploadDocument] Error:', {
        error: err.message,
        stack: err.stack,
        proId,
        documentType,
        fileName: file.name,
        fileSize: file.size
      })
      // Re-throw the error with context so it can be handled upstream
      throw new Error(`Document upload failed: ${err.message || 'Unknown error'}`)
    }
  },

  /**
   * Delete a document
   */
  async deleteDocument(filePath: string, client?: SupabaseClient): Promise<boolean> {
    const db = client || supabase
    try {
      console.log('[deleteDocument] Attempting to delete:', filePath)

      // Clean up the file path - ensure no leading/trailing slashes or spaces
      const cleanPath = filePath.trim().replace(/^\/+|\/+$/g, '')
      console.log('[deleteDocument] Cleaned path:', cleanPath)

      // No need to check auth - Supabase will handle it when we try to delete
      // First, check if the file exists
      const { data: listData, error: listError } = await db.storage
        .from('therapist-documents')
        .list(cleanPath.split('/').slice(0, -1).join('/'), {
          search: cleanPath.split('/').pop()
        })

      console.log('[deleteDocument] File exists check:', {
        listData,
        listError,
        searchPath: cleanPath.split('/').slice(0, -1).join('/'),
        searchFile: cleanPath.split('/').pop()
      })

      const { data, error } = await db.storage
        .from('therapist-documents')
        .remove([cleanPath])
      
      if (error) {
        console.error('[deleteDocument] Error:', error)
        console.error('[deleteDocument] Error details:', {
          message: error.message,
          statusCode: (error as any).statusCode,
          error: (error as any).error,
          path: cleanPath,
          originalPath: filePath
        })
        return false
      }
      
      console.log('[deleteDocument] Successfully deleted:', cleanPath)
      console.log('[deleteDocument] Delete response:', data)
      
      // Log document deletion for HIPAA compliance (non-blocking)
      try {
        // Get the proId from the file path (first part before /)
        const proId = cleanPath.split('/')[0];
        const fileName = cleanPath.split('/').pop() || '';
        
        // Extract document type from filename (e.g., 'backgroundCheck-1234567890.pdf' -> 'backgroundCheck')
        let documentType = 'unknown';
        if (fileName.includes('backgroundCheck')) documentType = 'backgroundCheck';
        else if (fileName.includes('crpoRegistration')) documentType = 'crpoRegistration';
        else if (fileName.includes('liabilityInsurance')) documentType = 'liabilityInsurance';
        
        if (proId) {
          auditService.logProAction(
            proId,
            'document.delete',
            'document',
            proId,
            {
              documentPath: cleanPath,
              fileName: fileName,
              documentType: documentType,
              timestamp: new Date().toISOString()
            },
            db
          ).catch(err => {
            console.error('[deleteDocument] Failed to log deletion:', err);
          });
        }
      } catch (err) {
        console.error('[deleteDocument] Failed to log deletion:', err);
      }
      
      return true
    } catch (error) {
      console.error('[deleteDocument] Error:', error)
      return false
    }
  },

  /**
   * Get a signed URL for a private document
   */
  async getDocumentSignedUrl(filePath: string, expiresIn: number = 3600): Promise<string | null> {
    try {
      console.log('[getDocumentSignedUrl] Creating signed URL for:', filePath)
      
      // Validate file path
      if (!filePath || filePath.trim() === '') {
        console.error('[getDocumentSignedUrl] Invalid file path:', filePath)
        return null
      }
      
      // Supabase handles auth internally, no need to check session
      // The client will automatically handle auth and refresh if needed
      
      // Create signed URL directly
      const { data, error } = await supabase.storage
        .from('therapist-documents')
        .createSignedUrl(filePath, expiresIn)
      
      if (error) {
        console.error('[getDocumentSignedUrl] Error:', error)
        console.error('[getDocumentSignedUrl] Error details:', {
          message: error.message,
          statusCode: (error as any).statusCode,
          path: filePath,
          bucket: 'therapist-documents'
        })
        
        // If it's a 404, the file might not exist
        if ((error as any).statusCode === 404) {
          console.error('[getDocumentSignedUrl] File not found in storage')
        }
        
        return null
      }
      
      if (!data || !data.signedUrl) {
        console.error('[getDocumentSignedUrl] No signed URL returned')
        return null
      }
      
      console.log('[getDocumentSignedUrl] Success:', data.signedUrl.substring(0, 50) + '...')
      return data.signedUrl
    } catch (error) {
      console.error('[getDocumentSignedUrl] Unexpected error:', error)
      return null
    }
  },

  /**
   * Delete old profile images for a user (cleanup)
   * Since we're using a fixed filename, this is now simpler
   */
  async deleteOldProfileImages(userId: string) {
    try {
      // List all files in the user's profile-images folder
      const { data: files, error: listError } = await supabase.storage
        .from('public-assets')
        .list(`profile-images/${userId}`)

      if (listError) {
        console.error('Error listing files:', listError)
        return
      }

      // Delete all files except the current headshot (which will be overwritten anyway)
      const filesToDelete = files
        ?.filter(file => !file.name.startsWith('headshot.'))
        .map(file => `profile-images/${userId}/${file.name}`) || []

      if (filesToDelete.length > 0) {
        const { error: deleteError } = await supabase.storage
          .from('public-assets')
          .remove(filesToDelete)

        if (deleteError) {
          console.error('Error deleting old images:', deleteError)
        }
      }
    } catch (err) {
      console.error('Error in deleteOldProfileImages:', err)
    }
  },

  /**
   * Get a public therapist profile by ID
   * This is used for the client-facing therapist profile pages
   */
  async getPublicTherapistById(id: string): Promise<PublicTherapistProfile | null> {
    try {
      const { data: pro, error } = await supabase
        .from('pros')
        .select(`
          *,
          supervisor:supervisorId (
            id,
            firstName,
            lastName,
            credentials,
            profileImage,
            startDate
          )
        `)
        .eq('id', id)
        .eq('isActive', true)
        .eq('isTherapist', true)
        .single()

      if (error || !pro) {
        console.error('Error fetching therapist:', error)
        return null
      }

      return transformToPublicTherapist(pro)
    } catch (err) {
      console.error('Error in getPublicTherapistById:', err)
      return null
    }
  },

  /**
   * Get Between Sessions therapists for the in-program referral
   * network. Pulls every Pro with `isBetweenSession = true` who isn't
   * a supervisor or admin — no `isActive` gate, since most members
   * haven't filled out the practice info that flips that flag, and
   * we'd rather show a profile than silently drop it.
   */
  async getBetweenSessionsTherapists(): Promise<PublicTherapistProfile[]> {
    try {
      const { data: pros, error } = await supabase
        .from('pros')
        .select(`
          *,
          supervisor:supervisorId (
            id,
            firstName,
            lastName,
            credentials,
            profileImage,
            startDate
          )
        `)
        .eq('isBetweenSession', true)
        .eq('isSupervisor', false)
        .eq('isAdmin', false)
        .is('deletedAt', null)
        .order('firstName', { ascending: true })

      if (error) {
        console.error('Error fetching BS therapists:', error)
        return []
      }
      return (pros || []).map(transformToPublicTherapist)
    } catch (err) {
      console.error('Error in getBetweenSessionsTherapists:', err)
      return []
    }
  },

  /**
   * Get all active therapists for the directory
   */
  async getActiveTherapists(): Promise<PublicTherapistProfile[]> {
    try {
      const { data: pros, error } = await supabase
        .from('pros')
        .select(`
          *,
          supervisor:supervisorId (
            id,
            firstName,
            lastName,
            credentials,
            profileImage,
            startDate
          )
        `)
        .eq('isActive', true)
        .eq('isTherapist', true)
        .is('deletedAt', null)
        .order('firstName', { ascending: true })

      if (error) {
        console.error('Error fetching therapists:', error)
        return []
      }

      return (pros || []).map(transformToPublicTherapist)
    } catch (err) {
      console.error('Error in getActiveTherapists:', err)
      return []
    }
  },

  /**
   * Get all therapists by cohort (for admin profile viewer)
   */
  async getTherapistsByCohort(cohort?: string) {
    console.log('[profileService.getTherapistsByCohort] Called with cohort:', cohort);
    
    // Supabase handles auth internally, no need to check session
    console.log('[profileService.getTherapistsByCohort] Fetching therapists for cohort:', cohort);
    
    try {
      let query = supabase
        .from('pros')
        .select(`
          id,
          authId,
          firstName,
          lastName,
          email,
          personalEmail,
          cohort,
          isActive,
          isTherapist,
          employmentStatus,
          startDate,
          profileImage,
          createdAt,
          updatedAt,
          phone,
          birthday,
          gender,
          pronoun,
          timezone,
          street,
          city,
          province,
          postalCode,
          credentials,
          education,
          languages,
          supervisorId,
          isApprovedForCouples,
          certifications,
          bio,
          uniqueUnderstanding,
          clientsDescribeMe,
          rightTherapistIf,
          expectToFeel,
          schedule,
          areasOfFocus,
          modalities,
          googleMeetLink,
          backgroundCheckUrl,
          crpoRegistrationUrl,
          liabilityInsuranceUrl
        `)
        .eq('isTherapist', true)
        .is('deletedAt', null)
        .order('createdAt', { ascending: false })

      if (cohort) {
        // Special handling for "Others" group - non-resident employment status
        if (cohort === 'Others') {
          query = query.neq('employmentStatus', 'RESIDENT')
        } else {
          query = query.eq('cohort', cohort)
        }
      }

      console.log('[profileService.getTherapistsByCohort] Executing query...');
      const { data, error } = await query

      if (error) {
        console.error('[profileService.getTherapistsByCohort] Database error:', error)
        console.error('[profileService.getTherapistsByCohort] Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        })
        return []
      }

      console.log('[profileService.getTherapistsByCohort] Query successful, received data:', {
        count: data?.length || 0,
        firstItem: data?.[0]
      })

      // Calculate completion for each therapist
      const therapistsWithCompletion = (data || []).map(therapist => {
        const completion = calculateProfileCompletion(therapist)
        return {
          ...therapist,
          completionPercentage: completion.percentage,
          completedSections: completion.completedSections,
          totalSections: completion.totalSections,
          missingSections: completion.missingSections
        }
      })

      return therapistsWithCompletion
    } catch (err) {
      console.error('Error in getTherapistsByCohort:', err)
      return []
    }
  },

  /**
   * Get distinct cohorts
   */
  async getCohorts() {
    try {
      // Get all therapists to check for non-residents
      const { data: allTherapists, error: allError } = await supabase
        .from('pros')
        .select('cohort, employmentStatus')
        .eq('isTherapist', true)
        .is('deletedAt', null)

      if (allError) {
        console.error('Error fetching all therapists:', allError)
        return []
      }

      // Get unique cohorts (excluding null values)
      const cohortsWithValues = allTherapists?.filter(d => d.cohort).map(d => d.cohort) || []
      const uniqueCohorts = [...new Set(cohortsWithValues)].sort((a, b) => b.localeCompare(a))
      
      // Check if there are any non-resident therapists (employment status not RESIDENT)
      const hasNonResidents = allTherapists?.some(d => 
        d.employmentStatus && d.employmentStatus !== 'RESIDENT'
      )
      
      if (hasNonResidents) {
        uniqueCohorts.push('Others')
      }
      
      return uniqueCohorts
    } catch (err) {
      console.error('Error in getCohorts:', err)
      return []
    }
  },

  /**
   * Update therapist active status (approve/unapprove profile)
   */
  async updateTherapistActiveStatus(authId: string, isActive: boolean) {
    try {
      console.log('[updateTherapistActiveStatus] Updating status for authId:', authId, 'to isActive:', isActive)
      
      const { data, error } = await supabase
        .from('pros')
        .update({ isActive, updatedAt: new Date().toISOString() })
        .eq('authId', authId)
        .select()
        .single()

      console.log('[updateTherapistActiveStatus] Supabase response:', { data, error })

      if (error) {
        console.error('[updateTherapistActiveStatus] Supabase error:', error)
        throw error
      }

      console.log('[updateTherapistActiveStatus] Successfully updated, new data:', data)
      return data
    } catch (err) {
      console.error('[updateTherapistActiveStatus] Caught error:', err)
      throw err
    }
  },

  /**
   * Get a therapist by ID (for preview in admin)
   */
  async getTherapistById(id: string) {
    try {
      const { data, error } = await supabase
        .from('pros')
        .select('*')
        .eq('id', id)
        .single()

      if (error) {
        console.error('Error fetching therapist by ID:', error)
        return null
      }

      return data
    } catch (err) {
      console.error('Error in getTherapistById:', err)
      return null
    }
  }
}

/**
 * Transform database record to ProfileData
 */
function transformToProfileData(dbRecord: any): ProfileData {
  // Clean and validate schedule data when loading from DB
  let cleanedSchedule = dbRecord.schedule;
  if (cleanedSchedule) {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const isValidSchedule = typeof cleanedSchedule === 'object' && 
      days.every(day => {
        const daySchedule = cleanedSchedule[day];
        return daySchedule && 
               typeof daySchedule.morning === 'boolean' &&
               typeof daySchedule.afternoon === 'boolean' &&
               typeof daySchedule.evening === 'boolean';
      });
    
    if (!isValidSchedule) {
      console.warn('[transformToProfileData] Found corrupted schedule data, using clean default', {
        authId: dbRecord.authId,
        corruptedSchedule: cleanedSchedule
      });
      // Use a clean default schedule structure
      cleanedSchedule = {
        monday: { morning: false, afternoon: false, evening: false },
        tuesday: { morning: false, afternoon: false, evening: false },
        wednesday: { morning: false, afternoon: false, evening: false },
        thursday: { morning: false, afternoon: false, evening: false },
        friday: { morning: false, afternoon: false, evening: false },
        saturday: { morning: false, afternoon: false, evening: false },
        sunday: { morning: false, afternoon: false, evening: false }
      };
    }
  }
  
  return {
    firstName: dbRecord.firstName,
    lastName: dbRecord.lastName,
    gender: dbRecord.gender,
    pronoun: dbRecord.pronoun,
    birthday: dbRecord.birthday,
    phone: dbRecord.phone,
    personalEmail: dbRecord.personalEmail,
    timezone: dbRecord.timezone,
    unit: dbRecord.unit,
    street: dbRecord.street,
    city: dbRecord.city,
    province: dbRecord.province,
    country: dbRecord.country,
    postalCode: dbRecord.postalCode,
    credentials: dbRecord.credentials,
    education: dbRecord.education,
    languages: dbRecord.languages,
    supervisorId: dbRecord.supervisorId,
    isApprovedForCouples: dbRecord.isApprovedForCouples,
    certifications: dbRecord.certifications,
    profileImage: dbRecord.profileImage,
    bio: dbRecord.bio,
    uniqueUnderstanding: dbRecord.uniqueUnderstanding,
    clientsDescribeMe: dbRecord.clientsDescribeMe,
    rightTherapistIf: dbRecord.rightTherapistIf,
    expectToFeel: dbRecord.expectToFeel,
    schedule: cleanedSchedule,
    areasOfFocus: dbRecord.areasOfFocus,
    modalities: dbRecord.modalities,
    googleMeetLink: dbRecord.googleMeetLink,
    backgroundCheckUrl: dbRecord.backgroundCheckUrl || null,
    crpoRegistrationUrl: dbRecord.crpoRegistrationUrl || null,
    liabilityInsuranceUrl: dbRecord.liabilityInsuranceUrl || null,
    isAdmin: dbRecord.isAdmin,
    isTherapist: dbRecord.isTherapist,
    isSupervisor: dbRecord.isSupervisor,
    employmentStatus: dbRecord.employmentStatus,
    isActive: dbRecord.isActive,
    isAcceptingIndividual: dbRecord.isAcceptingIndividual,
    isAcceptingCouples: dbRecord.isAcceptingCouples,
    startDate: dbRecord.startDate,
    bsExternalPractice: dbRecord.bsExternalPractice,
    supervisorProfile: dbRecord.supervisorProfile,
  }
}

/**
 * Map ProfileData to database fields
 */
function mapProfileDataToDb(data: Partial<ProfileData>): Record<string, any> {
  const mapped: Record<string, any> = {}
  
  // Only include fields that are defined
  if (data.firstName !== undefined) mapped.firstName = data.firstName
  if (data.lastName !== undefined) mapped.lastName = data.lastName
  if (data.gender !== undefined) mapped.gender = data.gender
  if (data.pronoun !== undefined) mapped.pronoun = data.pronoun
  if (data.birthday !== undefined) mapped.birthday = data.birthday
  if (data.phone !== undefined) mapped.phone = data.phone
  if (data.personalEmail !== undefined) mapped.personalEmail = data.personalEmail
  if (data.timezone !== undefined) mapped.timezone = data.timezone
  if (data.unit !== undefined) mapped.unit = data.unit
  if (data.street !== undefined) mapped.street = data.street
  if (data.city !== undefined) mapped.city = data.city
  if (data.province !== undefined) mapped.province = data.province
  if (data.country !== undefined) mapped.country = data.country
  if (data.postalCode !== undefined) mapped.postalCode = data.postalCode
  if (data.credentials !== undefined) mapped.credentials = data.credentials
  if (data.education !== undefined) mapped.education = data.education
  if (data.languages !== undefined) mapped.languages = data.languages
  if (data.supervisorId !== undefined) mapped.supervisorId = data.supervisorId
  if (data.isApprovedForCouples !== undefined) mapped.isApprovedForCouples = data.isApprovedForCouples
  if (data.certifications !== undefined) mapped.certifications = data.certifications
  if (data.profileImage !== undefined) {
    // Never save blob URLs to the database
    if (typeof data.profileImage === 'string' && data.profileImage.startsWith('blob:')) {
      console.warn('[mapProfileDataToDb] Skipping blob URL for profileImage')
      // Don't include the field at all
    } else {
      mapped.profileImage = data.profileImage
    }
  }
  if (data.bio !== undefined) mapped.bio = data.bio
  if (data.uniqueUnderstanding !== undefined) mapped.uniqueUnderstanding = data.uniqueUnderstanding
  if (data.clientsDescribeMe !== undefined) mapped.clientsDescribeMe = data.clientsDescribeMe
  if (data.rightTherapistIf !== undefined) mapped.rightTherapistIf = data.rightTherapistIf
  if (data.expectToFeel !== undefined) mapped.expectToFeel = data.expectToFeel
  if (data.schedule !== undefined) {
    // Ensure schedule is properly formatted as JSON
    if (data.schedule === null || data.schedule === undefined) {
      // If schedule is being cleared, set to null
      mapped.schedule = null
    } else if (typeof data.schedule === 'object') {
      // Validate the schedule structure before saving
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      const isValid = days.every(day => {
        const daySchedule = data.schedule[day]
        return daySchedule && 
               typeof daySchedule.morning === 'boolean' &&
               typeof daySchedule.afternoon === 'boolean' &&
               typeof daySchedule.evening === 'boolean'
      })
      
      if (isValid) {
        mapped.schedule = data.schedule
      } else {
        console.warn('[mapProfileDataToDb] Invalid schedule structure, using default')
        // Use a default valid structure if invalid
        mapped.schedule = {
          monday: { morning: false, afternoon: false, evening: false },
          tuesday: { morning: false, afternoon: false, evening: false },
          wednesday: { morning: false, afternoon: false, evening: false },
          thursday: { morning: false, afternoon: false, evening: false },
          friday: { morning: false, afternoon: false, evening: false },
          saturday: { morning: false, afternoon: false, evening: false },
          sunday: { morning: false, afternoon: false, evening: false }
        }
      }
    } else {
      console.warn('[mapProfileDataToDb] Invalid schedule type:', typeof data.schedule)
      mapped.schedule = null
    }
  }
  if (data.areasOfFocus !== undefined) mapped.areasOfFocus = data.areasOfFocus
  if (data.modalities !== undefined) mapped.modalities = data.modalities
  if (data.googleMeetLink !== undefined) mapped.googleMeetLink = data.googleMeetLink
  if (data.backgroundCheckUrl !== undefined) {
    mapped.backgroundCheckUrl = data.backgroundCheckUrl || null
    console.log('[mapProfileDataToDb] Mapping backgroundCheckUrl:', data.backgroundCheckUrl, '->', mapped.backgroundCheckUrl)
  }
  if (data.crpoRegistrationUrl !== undefined) {
    mapped.crpoRegistrationUrl = data.crpoRegistrationUrl || null
    console.log('[mapProfileDataToDb] Mapping crpoRegistrationUrl:', data.crpoRegistrationUrl, '->', mapped.crpoRegistrationUrl)
  }
  if (data.liabilityInsuranceUrl !== undefined) {
    mapped.liabilityInsuranceUrl = data.liabilityInsuranceUrl || null
    console.log('[mapProfileDataToDb] Mapping liabilityInsuranceUrl:', data.liabilityInsuranceUrl, '->', mapped.liabilityInsuranceUrl)
  }
  if (data.isAdmin !== undefined) mapped.isAdmin = data.isAdmin
  if (data.isTherapist !== undefined) mapped.isTherapist = data.isTherapist
  if (data.isSupervisor !== undefined) mapped.isSupervisor = data.isSupervisor
  if (data.employmentStatus !== undefined) mapped.employmentStatus = data.employmentStatus
  if (data.isActive !== undefined) mapped.isActive = data.isActive
  if (data.isAcceptingIndividual !== undefined) mapped.isAcceptingIndividual = data.isAcceptingIndividual
  if (data.isAcceptingCouples !== undefined) mapped.isAcceptingCouples = data.isAcceptingCouples
  if (data.startDate !== undefined) mapped.startDate = data.startDate
  if (data.bsExternalPractice !== undefined) mapped.bsExternalPractice = data.bsExternalPractice
  if (data.supervisorProfile !== undefined) mapped.supervisorProfile = data.supervisorProfile

  return mapped
}

/**
 * Map section data to ProfileData
 */
function mapSectionToProfileData(section: ProfileSection, data: any): Partial<ProfileData> {
  switch (section) {
    case 'personal':
      return {
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender,
        pronoun: data.pronoun,
        birthday: data.birthday,
        phone: data.phone,
        personalEmail: data.personalEmail,
        timezone: data.timezone
      }

    case 'address':
      return {
        unit: data.unit,
        street: data.street,
        city: data.city,
        province: data.province,
        country: data.country,
        postalCode: data.postalCode
      }

    case 'professional':
      console.log('[mapSectionToProfileData] Professional section data:', {
        education: data.education,
        allData: data
      })
      return {
        credentials: data.credentials,
        education: data.education,
        languages: data.languages,
        supervisorId: data.supervisorId,
        isApprovedForCouples: data.isApprovedForCouples,
        certifications: data.certifications
      }

    case 'photo':
      console.log('[mapSectionToProfileData] Photo section data:', {
        profileImage: data.profileImage
      })
      // Never return blob URLs
      if (typeof data.profileImage === 'string' && data.profileImage.startsWith('blob:')) {
        console.warn('[mapSectionToProfileData] Skipping blob URL in photo section')
        return {} // Return empty object to avoid saving blob URL
      }
      return {
        profileImage: data.profileImage
      }

    case 'profile':
      return {
        bio: data.bio,
        uniqueUnderstanding: data.uniqueUnderstanding,
        clientsDescribeMe: data.clientsDescribeMe,
        rightTherapistIf: data.rightTherapistIf,
        expectToFeel: data.expectToFeel
      }

    case 'practice':
      console.log('[mapSectionToProfileData] practice section data:', {
        schedule: data.schedule,
        areasOfFocus: data.areasOfFocus,
        areasOfFocusType: typeof data.areasOfFocus,
        modalities: data.modalities,
        googleMeetLink: data.googleMeetLink
      })
      return {
        schedule: data.schedule,
        areasOfFocus: data.areasOfFocus || {},
        modalities: data.modalities,
        googleMeetLink: data.googleMeetLink
      }

    case 'availability':
      console.log('[mapSectionToProfileData] availability section data:', {
        schedule: data.schedule,
        googleMeetLink: data.googleMeetLink
      })
      return {
        schedule: data.schedule,
        googleMeetLink: data.googleMeetLink
      }

    case 'expertise':
      console.log('[mapSectionToProfileData] expertise section data:', {
        areasOfFocus: data.areasOfFocus,
        areasOfFocusType: typeof data.areasOfFocus,
        modalities: data.modalities
      })
      return {
        areasOfFocus: data.areasOfFocus || {},
        modalities: data.modalities
      }

    case 'documents':
      console.log('[mapSectionToProfileData] Documents section data:', {
        backgroundCheckUrl: data.backgroundCheckUrl,
        crpoRegistrationUrl: data.crpoRegistrationUrl,
        liabilityInsuranceUrl: data.liabilityInsuranceUrl,
        allData: data
      })
      return {
        backgroundCheckUrl: data.backgroundCheckUrl,
        crpoRegistrationUrl: data.crpoRegistrationUrl,
        liabilityInsuranceUrl: data.liabilityInsuranceUrl
      }

    case 'supervisor':
      // Whitelist every allowed leaf key inside the supervisorProfile JSONB
      // before persisting so a client can't smuggle extra fields into the
      // blob (mirrors the practiceContext safety pattern in the BS
      // profile-builder route). Unknown keys on the submitted bag get
      // dropped silently.
      //
      // googleMeetLink piggybacks on this section because pure supervisors
      // (isSupervisor && !isTherapist) never see the Availability section
      // where it otherwise lives — Supervisor Profile is the one place
      // guaranteed to be reachable for them, and the supervision accept
      // flow reads `pros.googleMeetLink` for the session's Meet URL.
      return {
        supervisorProfile: pickSupervisorProfileKeys(data?.supervisorProfile),
        googleMeetLink: data.googleMeetLink,
      }

    default:
      throw new Error(`Invalid section: ${section}`)
  }
}

/**
 * Whitelist the shape of pros.supervisorProfile so any bogus keys on the
 * submitted bag are dropped before hitting the DB. Missing keys default
 * to sensible empty values so a partial save doesn't nuke unrelated
 * fields (the caller should merge with the current DB value if they
 * want additive semantics — this only strips, it doesn't merge).
 */
function pickSupervisorProfileKeys(bag: unknown): Record<string, unknown> | null {
  if (!bag || typeof bag !== 'object') return null
  const b = bag as Record<string, unknown>
  const strArr = (v: unknown) =>
    Array.isArray(v) ? v.filter(x => typeof x === 'string' && x.trim().length > 0) : []
  const str = (v: unknown) => (typeof v === 'string' ? v : '')
  const yearsRaw = b.yearsSupervising
  const years =
    yearsRaw === null || yearsRaw === undefined || yearsRaw === ''
      ? null
      : Number.isFinite(Number(yearsRaw))
        ? Math.max(0, Math.min(80, Math.trunc(Number(yearsRaw))))
        : null
  return {
    tagline: str(b.tagline),
    philosophy: str(b.philosophy),
    style: str(b.style),
    superviseesDescribeMe: strArr(b.superviseesDescribeMe),
    professionalBackground: str(b.professionalBackground),
    yearsSupervising: years,
    workSettings: strArr(b.workSettings),
    populationsWorkedWith: strArr(b.populationsWorkedWith),
    supervisionFormats: strArr(b.supervisionFormats),
    populationsSupervised: strArr(b.populationsSupervised),
    supervisionExperience: str(b.supervisionExperience),
    expectationsOfSupervisees: str(b.expectationsOfSupervisees),
    notTheFitIf: str(b.notTheFitIf),
  }
}

/**
 * Generate a URL-friendly slug from first and last name
 */
function generateTherapistSlug(firstName: string, lastName?: string): string {
  const fullName = lastName ? `${firstName} ${lastName}` : firstName
  return fullName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Transform database pro record to public therapist profile
 */
export function transformToPublicTherapist(pro: any): PublicTherapistProfile {
  // Calculate years of experience based on start date
  const startDate = pro.startDate ? new Date(pro.startDate) : null
  const yearsExperience = startDate 
    ? Math.floor((Date.now() - startDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : 0

  // Determine therapist tier based on employment status
  const therapistTier: 'resident' | 'associate' = pro.employmentStatus === 'RESIDENT' ? 'resident' : 'associate'

  // Parse credentials - handle both single and multiple credentials
  let credential = ''
  let credentialsList: string[] = []
  if (pro.credentials) {
    if (Array.isArray(pro.credentials) && pro.credentials.length > 0) {
      // Multiple credentials - join them with comma
      credentialsList = pro.credentials.map((cred: any) => cred.type)
      credential = credentialsList.join(', ')
    } else if (pro.credentials.type) {
      // Single credential (old format)
      credential = pro.credentials.type
      credentialsList = [pro.credentials.type]
    }
  }

  // Parse education from string or array
  const education = pro.education ? 
    (Array.isArray(pro.education) ? pro.education : [pro.education]) 
    : []

  // Transform areas of focus
  const areasOfFocus = transformAreasOfFocus(pro.areasOfFocus || {})

  // Transform modalities with descriptions
  const modalities = transformModalities(pro.modalities || [])

  // Parse schedule
  const schedule = pro.schedule || getDefaultSchedule()

  // Get supervisor info if resident
  let supervisorCredentialsList: string[] = []
  const supervisor = pro.supervisor ? {
    name: `${pro.supervisor.firstName} ${pro.supervisor.lastName || ''}`.trim(),
    credential: (() => {
      if (pro.supervisor.credentials) {
        if (Array.isArray(pro.supervisor.credentials) && pro.supervisor.credentials.length > 0) {
          supervisorCredentialsList = pro.supervisor.credentials.map((cred: any) => cred.type)
          return supervisorCredentialsList.join(', ')
        } else if (pro.supervisor.credentials.type) {
          supervisorCredentialsList = [pro.supervisor.credentials.type]
          return pro.supervisor.credentials.type
        }
      }
      return ''
    })(),
    credentials: supervisorCredentialsList,
    yearsExperience: pro.supervisor.startDate 
      ? Math.floor((Date.now() - new Date(pro.supervisor.startDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
      : 15,
    profileImage: pro.supervisor.profileImage || ''
  } : undefined

  return {
    id: pro.id,
    slug: generateTherapistSlug(pro.firstName, pro.lastName),
    firstName: pro.firstName,
    lastName: pro.lastName || '',
    displayName: `${pro.firstName} ${pro.lastName || ''}`.trim(),
    credential,
    credentials: credentialsList,
    therapistTier,
    profileImage: pro.profileImage || '',
    pronoun: pro.pronoun || undefined,

    // Quick facts
    yearsExperience,
    languages: pro.languages || ['English'],
    education,
    
    // About sections
    bio: pro.bio || '',
    therapeuticApproach: '', // This field doesn't exist in the database yet
    uniqueUnderstanding: pro.uniqueUnderstanding || [],
    clientDescriptions: pro.clientsDescribeMe || [],
    rightFitFor: pro.rightTherapistIf || [],
    whatToExpect: pro.expectToFeel || [],
    
    // Areas of Focus
    areasOfFocus,
    
    // Modalities
    modalities,
    
    // Client focus
    clientTypes: getClientTypes(pro),
    
    // Rates
    rates: {
      tier: therapistTier,
      range: therapistTier === 'resident' ? '$45 - $105' : '$115 - $175',
      duration: '50 minutes'
    },
    
    // Insurance - this would need to be added to the database
    insuranceProviders: ['Blue Cross', 'Green Shield', 'Manulife', 'Sun Life'],
    
    // Supervisor
    supervisor,
    
    // Residency completion - this would need to be added to the database
    residencyCompletion: undefined,
    
    // Availability
    availability: {
      status: (pro.isActive && (pro.isAcceptingIndividual || pro.isAcceptingCouples)) 
        ? 'Accepting new clients' 
        : 'Not accepting new clients',
      acceptingFor: getAcceptingFor(pro),
      waitTime: 'Usually within 1 week',
      sessionFormat: ['Virtual Sessions'],
      nextAvailable: 'Contact for availability',
      bookingNote: generateBookingNote(schedule),
      timezone: TIMEZONE_DISPLAY_NAMES[pro.timezone] || pro.timezone || 'Toronto Time (EST/EDT)',
      schedule
    },
    
    // Locations
    locations: 'Across Canada',
    serviceAreas: ['BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NB', 'NS', 'PE', 'NL', 'YT', 'NT', 'NU'],
    
    // Session-type acceptance flags — consumers gate the booking CTA per session
    // type (a therapist may be closed to new individual clients but still taking
    // couples, or vice versa).
    isApprovedForCouples: pro.isApprovedForCouples || false,
    isAcceptingIndividual: pro.isAcceptingIndividual || false,
    isAcceptingCouples: pro.isAcceptingCouples || false,

    // Between Sessions external-practice JSON. The type marks this
    // optional so older callers stay compatible, but downstream
    // pages (the public BS directory and the in-program referral
    // network) depend on it for rates, locations, and links.
    bsExternalPractice: pro.bsExternalPractice || undefined,
  }
}

/**
 * Transform areas of focus from database format to display format
 */
function transformAreasOfFocus(areasOfFocusData: any): { category: string; items: string[] }[] {
  // If it's already an array (old format), convert to categorized format
  if (Array.isArray(areasOfFocusData)) {
    return categorizeAreasOfFocus(areasOfFocusData)
  }
  
  // If it's an object with categories (new format), transform it
  if (typeof areasOfFocusData === 'object' && areasOfFocusData !== null) {
    return Object.entries(areasOfFocusData)
      .filter(([_, items]) => Array.isArray(items) && items.length > 0)
      .map(([category, items]) => ({
        category,
        items: items as string[]
      }))
  }
  
  return []
}

/**
 * Categorize a flat array of areas of focus
 */
function categorizeAreasOfFocus(areas: string[]): { category: string; items: string[] }[] {
  const categorized: { [key: string]: string[] } = {}
  
  areas.forEach(area => {
    // Find the category this area belongs to
    let foundCategory = 'General Mental Health'
    
    for (const [category, categoryAreas] of Object.entries(AREA_OF_FOCUS_CATEGORIES)) {
      if (categoryAreas.includes(area)) {
        foundCategory = category
        break
      }
    }
    
    if (!categorized[foundCategory]) {
      categorized[foundCategory] = []
    }
    categorized[foundCategory]!.push(area)
  })
  
  return Object.entries(categorized).map(([category, items]) => ({
    category,
    items
  }))
}

/**
 * Transform modalities with descriptions
 */
function transformModalities(modalities: string[]): { name: string; description: string }[] {
  return modalities.map(modality => ({
    name: modality,
    description: MODALITY_DESCRIPTIONS[modality] || `${modality} therapy approach`
  }))
}

/**
 * Get client types based on pro settings
 */
function getClientTypes(pro: any): string[] {
  const types = []
  types.push('Individual (16+)') // Always show individual therapy
  if (pro.isApprovedForCouples === true) types.push('Couples')
  return types
}

/**
 * Get accepting for array
 */
function getAcceptingFor(pro: any): string[] {
  const accepting = []
  if (pro.isAcceptingIndividual === true) accepting.push('Individual')
  if (pro.isAcceptingCouples === true) accepting.push('Couples')
  return accepting.length > 0 ? accepting : []
}

/**
 * Get default schedule
 */
function getDefaultSchedule() {
  return {
    monday: { morning: true, afternoon: true, evening: false },
    tuesday: { morning: true, afternoon: true, evening: true },
    wednesday: { morning: false, afternoon: true, evening: true },
    thursday: { morning: true, afternoon: true, evening: false },
    friday: { morning: true, afternoon: true, evening: false },
    saturday: { morning: false, afternoon: false, evening: false },
    sunday: { morning: false, afternoon: false, evening: false }
  }
}

/**
 * Generate booking note based on actual schedule
 */
function generateBookingNote(schedule: any): string {
  if (!schedule) return ''
  
  let hasEvening = false
  let hasWeekend = false
  
  // Check weekdays for evening
  const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  weekdays.forEach(day => {
    if (schedule[day] && schedule[day].evening === true) {
      hasEvening = true
    }
  })
  
  // Check weekends
  const weekends = ['saturday', 'sunday']
  weekends.forEach(day => {
    if (schedule[day]) {
      if (schedule[day].morning === true || schedule[day].afternoon === true || schedule[day].evening === true) {
        hasWeekend = true
      }
    }
  })
  
  if (hasEvening && hasWeekend) {
    return 'Evening and weekend appointments available'
  } else if (hasEvening) {
    return 'Evening appointments available'
  } else if (hasWeekend) {
    return 'Weekend appointments available'
  }
  
  return ''
}

/**
 * Calculate profile completion percentage and details
 */
function calculateProfileCompletion(profile: any): {
  percentage: number
  completedSections: string[]
  totalSections: number
  missingSections: string[]
} {
  const sections = {
    'Personal Info': [
      profile.firstName,
      profile.lastName,
      profile.gender,
      profile.pronoun,
      profile.birthday,
      profile.phone,
      profile.personalEmail,
      profile.timezone
    ],
    'Address': [
      profile.street,
      profile.city,
      profile.province,
      profile.postalCode
    ],
    'Professional': [
      profile.credentials,
      profile.education,
      profile.languages && profile.languages.length > 0,
      profile.supervisorId
    ],
    'Photo': [
      profile.profileImage
    ],
    'Profile': [
      profile.bio,
      profile.uniqueUnderstanding && profile.uniqueUnderstanding.length > 0,
      profile.clientsDescribeMe && profile.clientsDescribeMe.length > 0,
      profile.rightTherapistIf && profile.rightTherapistIf.length > 0,
      profile.expectToFeel && profile.expectToFeel.length > 0
    ],
    'Practice': [
      profile.schedule,
      profile.areasOfFocus && Object.keys(profile.areasOfFocus).length > 0,
      profile.modalities && profile.modalities.length > 0,
      profile.googleMeetLink
    ],
    'Documents': [
      profile.backgroundCheckUrl,
      profile.crpoRegistrationUrl,
      profile.liabilityInsuranceUrl
    ]
  }

  const completedSections: string[] = []
  const missingSections: string[] = []
  let totalFields = 0
  let completedFields = 0

  Object.entries(sections).forEach(([sectionName, fields]) => {
    const sectionTotal = fields.length
    const sectionCompleted = fields.filter(field => {
      if (typeof field === 'boolean') return field
      if (typeof field === 'object' && field !== null) {
        if (Array.isArray(field)) return field.length > 0
        return Object.keys(field).length > 0
      }
      return !!field
    }).length

    totalFields += sectionTotal
    completedFields += sectionCompleted

    if (sectionCompleted === sectionTotal) {
      completedSections.push(sectionName)
    } else if (sectionCompleted === 0) {
      missingSections.push(sectionName)
    } else {
      missingSections.push(`${sectionName} (${sectionCompleted}/${sectionTotal})`)
    }
  })

  const percentage = Math.round((completedFields / totalFields) * 100)

  return {
    percentage,
    completedSections,
    totalSections: Object.keys(sections).length,
    missingSections
  }
}